import React, { useEffect, useState } from 'react';
import '@/App.css';
import '@/i18n';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CookieBanner from '@/components/CookieBanner';
import ScrollToTop from '@/components/ScrollToTop';
import Home from '@/pages/Home';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiePolicy from '@/pages/CookiePolicy';
import Terms from '@/pages/Terms';
import ThankYou from '@/pages/ThankYou';
import { hasConsentChoice } from '@/utils/consent';

function LangShell() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const validLang = lang === 'hr' || lang === 'en' ? lang : 'hr';

  const [cookieOpen, setCookieOpen] = useState(false);
  const [cookieMode, setCookieMode] = useState('banner');

  useEffect(() => {
    if (i18n.language !== validLang) i18n.changeLanguage(validLang);
    document.documentElement.setAttribute('lang', validLang);
  }, [validLang, i18n]);

  useEffect(() => {
    // Update document title / description per language
    const isLegal = /\/(privacy|cookies|terms)$/.test(location.pathname);
    const t = i18n.t.bind(i18n);
    let key = 'meta.home_title';
    let desc = 'meta.home_description';
    if (location.pathname.endsWith('/privacy')) {
      key = 'privacy.title';
    } else if (location.pathname.endsWith('/cookies')) {
      key = 'cookiepolicy.title';
    } else if (location.pathname.endsWith('/terms')) {
      key = 'terms.title';
    }
    document.title = isLegal ? `${t(key)} — Aurelqen` : t(key);
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t(desc));
  }, [location.pathname, i18n, validLang]);

  useEffect(() => {
    if (!hasConsentChoice()) {
      const id = setTimeout(() => setCookieOpen(true), 400);
      return () => clearTimeout(id);
    }
  }, []);

  const openPrefs = () => {
    setCookieMode('prefs');
    setCookieOpen(true);
  };

  if (lang !== 'hr' && lang !== 'en') {
    return <Navigate to="/hr" replace />;
  }

  return (
    <>
      <Header lang={validLang} />
      <Outlet context={{ lang: validLang }} />
      <Footer lang={validLang} onOpenCookies={openPrefs} />
      <CookieBanner
        open={cookieOpen}
        initialMode={cookieMode}
        onClose={() => {
          setCookieOpen(false);
          setCookieMode('banner');
        }}
      />
    </>
  );
}

function LangHome() {
  const { lang } = useParams();
  return <Home lang={lang} />;
}
function LangPrivacy() {
  const { lang } = useParams();
  return <PrivacyPolicy lang={lang} />;
}
function LangCookies() {
  const { lang } = useParams();
  return <CookiePolicy lang={lang} />;
}
function LangTerms() {
  const { lang } = useParams();
  return <Terms lang={lang} />;
}

function App() {
  return (
    <div className="aq-app">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Navigate to="/hr" replace />} />
          <Route path="/thank_you" element={<ThankYouShell />} />
          <Route path="/:lang" element={<LangShell />}>
            <Route index element={<LangHome />} />
            <Route path="privacy" element={<LangPrivacy />} />
            <Route path="cookies" element={<LangCookies />} />
            <Route path="terms" element={<LangTerms />} />
          </Route>
          <Route path="*" element={<Navigate to="/hr" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function ThankYouShell() {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== 'hr') i18n.changeLanguage('hr');
    document.documentElement.setAttribute('lang', 'hr');
    document.title = 'Hvala vam — Aurelqen';
  }, [i18n]);
  return <ThankYou />;
}

export default App;
